import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { useNavigate } from 'react-router-dom';
import { Plus, Wallet, Building2, FileText, ArrowRight, Upload, AlertTriangle, Check, ChevronLeft, ChevronRight, CreditCard, Trash2, Pencil, Loader2, Sparkles, RefreshCw, X, Receipt, Download, FileSpreadsheet, FileDown } from 'lucide-react';
import { toast } from 'sonner';
import { ekstreIndir } from '@/lib/ekstre';
import { dekontuAc } from '@/lib/dekont';
import DekontDialog from '@/components/DekontDialog';
import EkDugmesi, { EkSecici, kayittanSonraYukle } from '@/components/HareketEkleri';
import { trNorm } from '@/lib/arama';
import { YENI_HESAP_TURLERI, VARSAYILAN_HESAP_TURU } from '@/lib/hesapTurleri';
import CekSenet from '@/components/CekSenet';


const CURRENCIES = ['TRY', 'USD', 'EUR', 'GBP'];
const TX_TYPES = [
  { key: 'transfer', label: 'Hesaplar Arası Transfer' },
  { key: 'cari',     label: 'Ödeme' },
  { key: 'fx',       label: 'Döviz İşlemi' },
  { key: 'iade',     label: 'İade' },
  { key: 'satis',    label: 'Satış Gelirleri' },
];

const fmtRate = (n) => n == null ? '' : Number(n).toLocaleString('tr-TR', { minimumFractionDigits: 4, maximumFractionDigits: 4 });
const fmtTR = (n, d = 2) => n == null ? '—' : Number(n).toLocaleString('tr-TR', { minimumFractionDigits: d, maximumFractionDigits: d });

// ── Ekstre eşleştirme önerisi: benzer açıklamalı satırları tespit et ──
// Fatura/referans numaraları hariç, açıklamanın "şablonunu" karşılaştırır — böylece
// "İşCep PU Fatura 00463277 /KIB-TEK /FATURA NO:054599762" ile aynı karşı hesaba ait
// başka bir ay/referans no'lu satır (sayılar dışında aynı kalıp) benzer sayılır.
const SIMILARITY_THRESHOLD = 0.6;
const normalizeDescForMatch = (desc) => (desc || '')
  .toLowerCase()
  .replace(/[çğıöşü]/g, c => ({ ç: 'c', ğ: 'g', ı: 'i', ö: 'o', ş: 's', ü: 'u' }[c]))
  .replace(/\d+/g, '#')
  .replace(/[^a-z#\s]/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();
const descSimilarity = (a, b) => {
  const na = normalizeDescForMatch(a), nb = normalizeDescForMatch(b);
  if (!na || !nb) return 0;
  if (na === nb) return 1;
  const wa = new Set(na.split(' ').filter(w => w.length > 1));
  const wb = new Set(nb.split(' ').filter(w => w.length > 1));
  if (wa.size === 0 || wb.size === 0) return 0;
  const common = [...wa].filter(w => wb.has(w)).length;
  const union = new Set([...wa, ...wb]).size;
  return common / union;
};

function SearchableSelect({ options, value, onChange, placeholder }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef(null);
  const selected = options.find(o => o.id === value);
  const filtered = options.filter(o => trNorm(o.name).includes(trNorm(query))).slice(0, 60);
  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  return (
    <div ref={ref} className="relative">
      <input type="text"
        value={open ? query : (selected?.name || '')}
        onChange={e => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => { setQuery(''); setOpen(true); }}
        placeholder={placeholder || 'Seç veya ara...'}
        className="w-full h-9 px-3 rounded-md border border-[#d0c4b0] bg-white text-sm focus:outline-none focus:border-[#3d6e4e] focus:ring-1 focus:ring-[#3d6e4e]/30" />
      {open && (
        <div className="absolute z-[300] top-full left-0 right-0 mt-0.5 bg-white border border-[#d0c4b0] rounded-md shadow-lg max-h-52 overflow-y-auto">
          <div onMouseDown={() => { onChange(''); setQuery(''); setOpen(false); }}
            className="px-3 py-1.5 text-xs text-[#8a7e70] cursor-pointer hover:bg-[#f5f0e8] border-b border-[#e8dfd0]">— Boş bırak —</div>
          {filtered.length === 0
            ? <div className="px-3 py-2 text-xs text-[#8a7e70]">Sonuç bulunamadı</div>
            : filtered.map(o => (
              <div key={o.id} onMouseDown={() => { onChange(o.id); setQuery(''); setOpen(false); }}
                className={`px-3 py-1.5 text-xs cursor-pointer hover:bg-[#f5f0e8] ${o.id === value ? 'bg-[#e8dfd0] font-semibold' : ''}`}>
                {o.name}
              </div>
            ))}
        </div>
      )}
    </div>
  );
}

export default function TreasuryDashboard() {
  // Silme `delete` ister (sunucu: DELETE -> delete, 2026-09-28); yetkisi
  // olmayana 403'e götüren düğme gösterilmez.
  const { hasPermission } = useAuth();
  const silmeYetkisi = hasPermission('treasury', 'delete');
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  // Hesap TABLOSU ayrı bir liste kullanıyor: `accounts` açılır menüler için
  // hesap başına TEK satıra indirgeniyor, tabloda ise hesabın HER para birimi
  // ayrı satır olmalı (bkz. fetchAll).
  const [hesapSatirlari, setHesapSatirlari] = useState([]);
  const [allSuppliers, setAllSuppliers] = useState([]);
  const [expenseItems, setExpenseItems] = useState([]);
  const [transactions, setTransactions] = useState({ items: [], total: 0 });
  const [showTxn, setShowTxn] = useState(false);
  const [showAccount, setShowAccount] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [txType, setTxType] = useState('transfer');
  const [txnForm, setTxnForm] = useState({ transaction_date: new Date().toISOString().split('T')[0], debit_account_id: '', credit_account_id: '', amount: '', currency: 'TRY', description: '', supplier_id: '', create_ledger_entry: false });
  const [fxOut, setFxOut] = useState({ accountId: '', amount: '', currency: 'USD' });
  const [fxIn, setFxIn]   = useState({ accountId: '', amount: '', currency: 'TRY' });
  const [fxDescSuffix, setFxDescSuffix] = useState('');
  const [acctForm, setAcctForm] = useState({ name: '', currency: 'TRY' });
  // Sütun filtreleri de bu nesnede tutulur; her değişiklikte sunucudan yeniden
  // çekilir (istemcide süzmek yalnızca açık sayfayı süzer, sayaç yanıltır).
  const BOS_FILTRE = {
    account_id: 'all', page: 1,
    date_from: '', date_to: '',
    amount_op: '', amount: '', amount2: '', currency: '', tek_tarafli: false,
    debit_search: '', credit_search: '', description: '',
  };
  const [txnFilter, setTxnFilter] = useState(BOS_FILTRE);
  // Yazarken her tuşta istek atmasın diye filtreler geciktirilir
  const [filtreTaslak, setFiltreTaslak] = useState(BOS_FILTRE);
  const filtreZaman = useRef(null);
  const [editTxn, setEditTxn] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importData, setImportData] = useState(null);
  const [importAccountId, setImportAccountId] = useState('');
  const [importFileName, setImportFileName] = useState('');
  // Ekstre ekranından çıkmadan cari oluşturma
  const [newSupplier, setNewSupplier] = useState({ open: false, rowIndex: null, name: '', account_type: VARSAYILAN_HESAP_TURU, saving: false });
  const [importError, setImportError] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const importFileRef = useRef(null);

  useEffect(() => { fetchAll(); }, []);
  useEffect(() => { fetchTxns(); }, [txnFilter]);

  const fetchAll = () => {
    api.get('/suppliers').then(r => {
      const seen = new Set(); const uniq = [];
      r.data.forEach(x => { if (!seen.has(x.id)) { seen.add(x.id); uniq.push(x); } });
      setAllSuppliers(uniq);
      setAccounts(uniq.filter(s => s.account_type === 'Kasa ve Bankalar'));
      // `/suppliers` her (hesap, para birimi) için AYRI satır döndürüyor.
      // Tablo bir süre id'ye göre tekilleştirilmiş listeyi gösterdi ve hesabın
      // RASTGELE bir para birimindeki bakiyesi görünüyordu: Ana Kasa Nakit
      // "GBP -800" yazıyordu, TRY -118.798 hiç görünmüyordu (2026-09-20).
      setHesapSatirlari(r.data.filter(x => x.account_type === 'Kasa ve Bankalar'));
    }).catch(() => {});
    api.get('/expense-items').then(r => setExpenseItems(r.data)).catch(() => {});
  };
  // HESAP BAŞINA GRUPLAMA — Cari Hesaplar listesiyle aynı desen: hesap adı bir
  // kez, para birimleri altında "└ EUR bakiye" olarak. `/suppliers` her
  // (hesap, para birimi) için ayrı satır döndürüyor; düz listelemek aynı hesabı
  // dört kez yan yana yazıyordu (2026-09-21).
  const hesapGruplari = (() => {
    const gruplar = new Map();
    for (const a of hesapSatirlari) {
      if (!gruplar.has(a.id)) gruplar.set(a.id, { id: a.id, name: a.name, kurlar: [] });
      gruplar.get(a.id).kurlar.push(a);
    }
    // Para birimi sırası SABİT olsun: sunucu bunları bir kümeden üretiyor ve
    // sıra her açılışta değişebiliyor. TRY başta, kalanlar alfabetik.
    for (const g of gruplar.values()) {
      g.kurlar.sort((x, y) => {
        const a1 = x.currency || 'TRY', b1 = y.currency || 'TRY';
        if (a1 === b1) return 0;
        if (a1 === 'TRY') return -1;
        if (b1 === 'TRY') return 1;
        return a1.localeCompare(b1);
      });
    }
    return [...gruplar.values()];
  })();

  // Hesap listesi dökümü — cari listesiyle AYNI uç, yalnızca sütun seti farklı
  const hesapDokumu = (bicim) => ekstreIndir({
    yol: '/suppliers/liste-dokumu',
    bicim,
    satirIdler: hesapGruplari.flatMap(g => g.kurlar.map(a => `${a.id}|${a.currency || 'TRY'}`)),
    ekGovde: { sutun_seti: 'kasa', dosya_adi: 'Kasa-Banka' },
    bosMesaj: 'Listelenecek hesap yok',
    tur: 'listesi',
  });

  const fetchTxns = () => {
    const f = txnFilter;
    const p = { page: f.page, per_page: 25 };
    if (f.account_id !== 'all') p.account_id = f.account_id;
    if (f.date_from) p.date_from = f.date_from;
    if (f.date_to) p.date_to = f.date_to;
    if (f.amount_op && f.amount !== '' && f.amount != null) {
      p.amount_op = f.amount_op;
      p.amount = parseFloat(String(f.amount).replace(',', '.'));
      if (f.amount_op === 'between' && f.amount2 !== '') {
        p.amount2 = parseFloat(String(f.amount2).replace(',', '.'));
      }
    }
    if (f.currency) p.currency = f.currency;
    if (f.tek_tarafli) p.tek_tarafli = true;
    if (f.debit_search) p.debit_search = f.debit_search;
    if (f.credit_search) p.credit_search = f.credit_search;
    if (f.description) p.description = f.description;
    api.get('/treasury/transactions', { params: p }).then(r => setTransactions(r.data)).catch(() => {});
  };

  // Filtre kutularına yazarken 400 ms bekle, sonra sorgula. Tarih ve
  // karşılaştırma seçimi gibi ANLIK değişimlerde beklemeye gerek yok.
  const filtreDegistir = (alan, deger, anlik = false) => {
    setFiltreTaslak(prev => {
      const yeni = { ...prev, [alan]: deger, page: 1 };
      clearTimeout(filtreZaman.current);
      if (anlik) setTxnFilter(y => ({ ...y, ...yeni }));
      else filtreZaman.current = setTimeout(() => setTxnFilter(y => ({ ...y, ...yeni })), 400);
      return yeni;
    });
  };

  const filtreleriTemizle = () => {
    clearTimeout(filtreZaman.current);
    setFiltreTaslak(BOS_FILTRE);
    setTxnFilter(BOS_FILTRE);
  };

  const filtreVarMi = ['date_from', 'date_to', 'amount_op', 'currency', 'debit_search',
    'credit_search', 'description', 'tek_tarafli'].some(k => filtreTaslak[k]) || filtreTaslak.account_id !== 'all';

  // Seçenekler sunucudan gelir (şirkette fiilen kullanılan para birimleri)
  const paraBirimleri = transactions.currencies || ['TRY'];

  // Dekont: ortak `lib/dekont.js` (aynı akış cari ve kasa ekstresinde de var)
  const [dekontlar, setDekontlar] = useState(null);   // kayıt sonrası: [{id, etiket}]
  const [dekontMesgul, setDekontMesgul] = useState(null);

  const dekontuGoster = async (id, indir) => {
    setDekontMesgul(id + (indir ? '-i' : '-g'));
    await dekontuAc(id, indir);
    setDekontMesgul(null);
  };

  const handleDeleteTransaction = async (txnId) => {
    if (!window.confirm('Bu hareketi silmek istediğinize emin misiniz? Bağlı cari kayıt da silinecektir.')) return;
    try {
      await api.delete(`/treasury/transactions/${txnId}`);
      toast.success('Hareket silindi');
      fetchTxns();
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Silinemedi');
    }
  };

  const saveEditTxn = async () => {
    if (!editTxn) return;
    setLoading(true);
    try {
      await api.patch(`/treasury/transactions/${editTxn.id}`, {
        transaction_date: editTxn.transaction_date,
        debit_account_id: editTxn.debit_account_id || '',
        credit_account_id: editTxn.credit_account_id || '',
        amount: parseFloat(String(editTxn.amount).replace(',', '.')),
        currency: editTxn.currency,
        description: editTxn.description || '',
      });
      toast.success('Hareket güncellendi');
      setEditTxn(null);
      fetchTxns();
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Güncellenemedi');
    }
    setLoading(false);
  };

  // Yeni harekete eklenecek dosyalar (banka dekontu, makbuz) — kayıttan SONRA yüklenir
  const [yeniEkler, setYeniEkler] = useState([]);

  const saveTxn = async () => {
    setLoading(true);
    const yeniDekontlar = [];
    try {
      if (txType === 'fx') {
        const outAmt = parseFloat(String(fxOut.amount).replace(',', '.'));
        const inAmt  = parseFloat(String(fxIn.amount).replace(',', '.'));
        if (!outAmt || !inAmt) { toast.error('Her iki tutarı da girin'); setLoading(false); return; }
        const rate = fxOut.currency !== fxIn.currency ? inAmt / outAmt : null;
        const rateText = rate ? `1 ${fxOut.currency} = ${fmtRate(rate)} ${fxIn.currency}` : '';
        const desc = [rateText, fxDescSuffix].filter(Boolean).join(' | ');
        const cikan = await api.post('/treasury/transactions', { transaction_date: txnForm.transaction_date, debit_account_id: '', credit_account_id: fxOut.accountId, amount: outAmt, currency: fxOut.currency, description: desc, exchange_rate: 1 });
        const giren = await api.post('/treasury/transactions', { transaction_date: txnForm.transaction_date, debit_account_id: fxIn.accountId, credit_account_id: '', amount: inAmt, currency: fxIn.currency, description: desc, exchange_rate: 1 });
        toast.success('2 hareket kaydedildi');
        // Döviz işlemi iki bacak yazar; her bacağın kendi dekontu var
        if (cikan.data?.id) yeniDekontlar.push({ id: cikan.data.id, etiket: `Çıkan ${fxOut.currency}` });
        if (giren.data?.id) yeniDekontlar.push({ id: giren.data.id, etiket: `Giren ${fxIn.currency}` });
      } else {
        const r = await api.post('/treasury/transactions', { ...txnForm, amount: parseFloat(txnForm.amount) });
        toast.success('Hareket kaydedildi');
        if (r.data?.id) yeniDekontlar.push({ id: r.data.id, etiket: 'Bu hareket' });
      }
      setShowTxn(false);
      setTxnForm(p => ({ ...p, transaction_date: new Date().toISOString().split('T')[0], debit_account_id: '', credit_account_id: '', amount: '', description: '' }));
      setFxOut({ accountId: '', amount: '', currency: 'USD' });
      setFxIn({ accountId: '', amount: '', currency: 'TRY' });
      setFxDescSuffix('');
      setTxType('transfer');
      const ekler = yeniEkler;
      setYeniEkler([]);
      await kayittanSonraYukle(yeniDekontlar.map(d => d.id), ekler);
      fetchAll(); fetchTxns();
      if (yeniDekontlar.length) setDekontlar(yeniDekontlar);
    } catch (err) { toast.error('Hata'); }
    setLoading(false);
  };
  const saveAccount = async () => {
    if (!acctForm.name.trim()) return;
    setLoading(true);
    try {
      await api.post('/suppliers', { name: acctForm.name, currency: acctForm.currency, account_type: 'Kasa ve Bankalar', is_active: true });
      toast.success('Hesap oluşturuldu');
      setShowAccount(false);
      setAcctForm({ name: '', currency: 'TRY' });
      fetchAll();
    } catch (err) { toast.error('Hata'); }
    setLoading(false);
  };

  const processStatementFile = async (file) => {
    if (!file) return;
    if (!importAccountId) { toast.error('Önce hedef hesabı seçin'); return; }
    const ext = file.name.toLowerCase().split('.').pop();
    if (!['xlsx', 'xls', 'csv'].includes(ext)) {
      setImportError('Sadece Excel (.xlsx) veya CSV dosyası yükleyebilirsiniz.');
      return;
    }
    setImportError('');
    setImportFileName(file.name);
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('account_id', importAccountId);
      const res = await api.post(`/treasury/bank-statement/parse?account_id=${importAccountId}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (!res.data?.items?.length) {
        setImportError('Dosyada okunabilir hareket bulunamadı. Dosya formatını kontrol edin.');
      } else {
        // Banka masrafi onerisi olan satirlar varsayilan olarak isaretli gelsin
        // (kullanici isterse kapatabilir/degistirebilir)
        const items = res.data.items.map(it => ({
          ...it,
          is_expense: !!it.suggested_expense_item_id,
          expense_item_id: it.suggested_expense_item_id || '',
          invoice_number: it.extracted_invoice_number || '',
          // Mükerrer olmayan satırlar varsayılan seçili gelir; kullanıcı kaldırabilir
          selected: !it.is_duplicate,
          imported: false,
        }));
        setImportData({ ...res.data, items });
        toast.success(`${res.data.total} satır okundu`);
      }
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Ekstre okunamadı';
      setImportError(msg);
      toast.error(msg);
    }
    setLoading(false);
    if (importFileRef.current) importFileRef.current.value = '';
  };

  const handleImportFile = (e) => processStatementFile(e.target.files?.[0]);

  const updateImportItemSupplier = (idx, supplierId) => {
    setImportData(prev => {
      const items = [...prev.items];
      const supplier = allSuppliers.find(s => s.id === supplierId);
      const changedDesc = items[idx].description;
      items[idx] = {
        ...items[idx], matched_supplier_id: supplierId || null, matched_supplier_name: supplier?.name || null,
        suggested_supplier_id: null, suggested_supplier_name: null,
      };
      // Benzer aciklamali diger satirlara oneri yay (kullanicinin zaten farkli
      // bir sekilde esletirdigi satirlara dokunma)
      if (supplierId && supplier) {
        items.forEach((it, i) => {
          if (i === idx || it.matched_supplier_id === supplierId) return;
          if (descSimilarity(changedDesc, it.description) >= SIMILARITY_THRESHOLD) {
            items[i] = { ...it, suggested_supplier_id: supplierId, suggested_supplier_name: supplier.name };
          }
        });
      }
      return { ...prev, items };
    });
  };

  const applyImportSuggestion = (idx) => {
    setImportData(prev => {
      const items = [...prev.items];
      const it = items[idx];
      items[idx] = {
        ...it, matched_supplier_id: it.suggested_supplier_id, matched_supplier_name: it.suggested_supplier_name,
        suggested_supplier_id: null, suggested_supplier_name: null,
      };
      return { ...prev, items };
    });
  };

  const applyAllImportSuggestions = () => {
    setImportData(prev => ({
      ...prev,
      items: prev.items.map(it => it.suggested_supplier_id
        ? { ...it, matched_supplier_id: it.suggested_supplier_id, matched_supplier_name: it.suggested_supplier_name, suggested_supplier_id: null, suggested_supplier_name: null }
        : it),
    }));
  };

  // Ekstre eşleştirirken sistemde olmayan bir cari fark edilirse, sayfadan
  // ÇIKMADAN (yapılan eşleştirmeler kaybolmadan) yeni cari oluşturulur ve
  // hem listeye hem ilgili satıra anında yansıtılır.
  const createSupplierInline = async () => {
    const name = (newSupplier.name || '').trim();
    if (!name) { toast.error('Cari adı girin'); return; }
    setNewSupplier(p => ({ ...p, saving: true }));
    try {
      const res = await api.post('/suppliers', {
        name, account_type: newSupplier.account_type, is_active: true,
      });
      const created = res.data;
      // Listeye ekle (alfabetik sırayı koru) — yeniden fetch gerekmez
      setAllSuppliers(prev => [...prev, created].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'tr')));
      // Açılan satıra ata
      if (newSupplier.rowIndex != null) updateImportItemSupplier(newSupplier.rowIndex, created.id);
      toast.success(`"${created.name}" oluşturuldu ve satıra atandı`);
      setNewSupplier({ open: false, rowIndex: null, name: '', account_type: VARSAYILAN_HESAP_TURU, saving: false });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Cari oluşturulamadı');
      setNewSupplier(p => ({ ...p, saving: false }));
    }
  };

  // Başka bir sekmede cari açıldıysa listeyi tazelemek için (eşleştirmeler korunur)
  const refreshSuppliers = async () => {
    try {
      const r = await api.get('/suppliers');
      const seen = new Set(); const uniq = [];
      r.data.forEach(x => { if (!seen.has(x.id)) { seen.add(x.id); uniq.push(x); } });
      setAllSuppliers(uniq);
      toast.success('Cari listesi güncellendi');
    } catch { toast.error('Liste yenilenemedi'); }
  };

  // Banka masrafi/komisyon gibi cariye ait olmayan satirlari, kasa hareketine EK
  // olarak bir gider faturasi da acilacak sekilde isaretle.
  const toggleImportItemExpense = (idx, checked) => {
    setImportData(prev => {
      const items = [...prev.items];
      const it = items[idx];
      items[idx] = {
        ...it, is_expense: checked,
        expense_item_id: checked ? (it.expense_item_id || it.suggested_expense_item_id || '') : it.expense_item_id,
        invoice_number: checked && it.invoice_number == null ? (it.extracted_invoice_number || '') : it.invoice_number,
      };
      return { ...prev, items };
    });
  };

  const updateImportItemExpenseField = (idx, field, value) => {
    setImportData(prev => {
      const items = [...prev.items];
      items[idx] = { ...items[idx], [field]: value };
      return { ...prev, items };
    });
  };

  const handleImportDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    if (!importAccountId) { toast.error('Önce hedef hesabı seçin'); return; }
    processStatementFile(e.dataTransfer.files?.[0]);
  };

  const resetImport = () => {
    setImportData(null); setImportFileName(''); setImportError(''); setDragActive(false);
  };

  // Bir satır aktarılabilir mi?
  //
  // Mükerrer işareti bir UYARIDIR, yasak değil: tespit tarih + tutar + hesap
  // üçlüsüne bakıyor, aynı gün aynı tutarda gerçekten iki ayrı hareket olabilir
  // (iki kez aynı komisyon, aynı tutarlı iki havale). Kullanıcı itiraz edip
  // satırı yine de seçebilir; seçim onay ister.
  const isRowActionable = (it) => !it.imported;

  const [mukerrerOnay, setMukerrerOnay] = useState(null);   // {idx} | null

  const satiriSec = (idx, checked) => {
    setImportData(prev => ({
      ...prev,
      items: prev.items.map((it, i) => (i === idx && !it.imported)
        ? { ...it, selected: checked, duplicate_override: checked && it.is_duplicate ? true : it.duplicate_override }
        : it),
    }));
  };

  const toggleImportRow = (idx, checked) => {
    const it = importData?.items?.[idx];
    // Mükerrer bir satırı SEÇERKEN onay sor; seçimi kaldırmak serbest
    if (checked && it?.is_duplicate && !it?.duplicate_override) {
      setMukerrerOnay({ idx });
      return;
    }
    satiriSec(idx, checked);
  };

  // "Tümünü seç" mükerrerleri KAPSAMAZ — onlar tek tek, onayla seçilir.
  // Aksi halde tek tıkla mükerrer kayıt açılabilirdi.
  const toggleAllImportRows = (checked) => {
    setImportData(prev => ({
      ...prev,
      items: prev.items.map(it => (!it.imported && (!it.is_duplicate || it.duplicate_override))
        ? { ...it, selected: checked } : it),
    }));
  };

  // PARSİYEL AKTARIM: yalnızca seçili satırlar aktarılır. Aktarılanlar "imported"
  // olarak işaretlenip kilitlenir; pencere açık kalır, kalanlarla devam edilebilir.
  const confirmImport = async () => {
    const selectedIdx = importData.items
      .map((it, i) => (it.selected && isRowActionable(it)) ? i : -1)
      .filter(i => i >= 0);
    if (!selectedIdx.length) {
      toast.error('Aktarılacak satır seçilmedi');
      return;
    }
    const toImport = selectedIdx.map(i => importData.items[i]);
    // Dosya kaydı (tekrar-yükleme uyarısı için) yalnızca TÜM satırlar bittiğinde yazılır
    const remainingAfter = importData.items.filter(isRowActionable).length - toImport.length;
    const isFinalBatch = remainingAfter === 0;

    setLoading(true);
    try {
      const res = await api.post('/treasury/bank-statement/import', {
        account_id: importAccountId,
        items: toImport,
        ...(isFinalBatch ? { file_hash: importData.file_hash, file_name: importData.file_name } : {}),
      });
      // Aktarılanları kilitle
      const idxSet = new Set(selectedIdx);
      setImportData(prev => ({
        ...prev,
        items: prev.items.map((it, i) => idxSet.has(i) ? { ...it, imported: true, selected: false } : it),
      }));
      const expMsg = res.data.expense_invoices_created ? `, ${res.data.expense_invoices_created} gider faturası` : '';
      toast.success(`${res.data.imported} hareket aktarıldı${expMsg}` +
        (remainingAfter > 0 ? ` — ${remainingAfter} satır kaldı` : ''));
      fetchAll(); fetchTxns();
      if (isFinalBatch) { setShowImport(false); resetImport(); }
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Aktarım hatası');
    }
    setLoading(false);
  };

  const allTargets = allSuppliers.map(s => ({ id: s.id, name: s.name }));

  const totalPages = Math.ceil(transactions.total / 25);

  return (
    <div data-testid="treasury-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-[#1a1916] tracking-tight">Kasa / Banka</h1>
        <div className="flex gap-2">
          <Button size="sm" onClick={() => setShowImport(true)} variant="outline" className="border-[#d0c4b0] text-[#3F3E39]"><Upload size={14} className="mr-1" /> Ekstre Aktar</Button>
          <Button size="sm" onClick={() => setShowTxn(true)} className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916]"><Plus size={14} className="mr-1" /> Yeni Hareket</Button>
        </div>
      </div>

      <Tabs defaultValue="accounts">
        <TabsList className="mb-6">
          <TabsTrigger value="accounts">Hesaplar</TabsTrigger>
          <TabsTrigger value="transactions">Hareketler</TabsTrigger>
          <TabsTrigger value="checks">Çekler ve Senetler</TabsTrigger>
        </TabsList>

        {/* ── ACCOUNTS ── */}
        <TabsContent value="accounts">
          <div className="flex justify-end gap-2 mb-4">
            <Button size="sm" variant="outline" onClick={() => hesapDokumu('xlsx')}
              className="border-[#d0c4b0] text-[#3d6e4e] hover:bg-[#3d6e4e]/10"
              title="Hesap listesini Excel olarak indir" data-testid="hesap-excel">
              <FileSpreadsheet size={14} className="mr-1" /> Excel
            </Button>
            <Button size="sm" variant="outline" onClick={() => hesapDokumu('pdf')}
              className="border-[#d0c4b0] text-[#7a3535] hover:bg-[#7a3535]/10"
              title="Hesap listesini PDF olarak indir" data-testid="hesap-pdf">
              <FileDown size={14} className="mr-1" /> PDF
            </Button>
            <Button size="sm" variant="outline" onClick={() => setShowAccount(true)} className="border-[#d0c4b0]"><Plus size={14} className="mr-1" /> Yeni Hesap</Button>
          </div>
          <Card className="bg-[#faf7f2] border-[#d0c4b0]">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#e8dfd0] hover:bg-[#e8dfd0]">
                    <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Hesap Adı</TableHead>
                    <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Para Birimi</TableHead>
                    <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right">Bakiye</TableHead>
                    <TableHead className="w-8"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {hesapGruplari.map(grup => grup.kurlar.map((a, i) => {
                    const ilk = i === 0;
                    return (
                      <TableRow key={`${a.id}-${a.currency}`}
                        className={`erp-table-row border-[#d0c4b0] cursor-pointer group ${ilk ? '' : 'bg-[#f5f0e8]/50'}`}
                        onClick={() => navigate(`/erp/suppliers/${a.id}`)}>
                        {/* Hesap adı ve simge yalnızca ilk satırda; alt satırlar
                            hangi para birimi olduğunu söyler (Cari Hesaplar ile aynı) */}
                        <TableCell className="font-medium text-[#1a1916]">
                          {ilk ? (
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-lg bg-[#2e2d29]/10 flex items-center justify-center">
                                {(grup.name.toLowerCase().includes('kasa')) ? <Wallet size={16} className="text-[#2e2d29]" /> :
                                 grup.name.toLowerCase().includes('pos') ? <CreditCard size={16} className="text-[#2e2d29]" /> :
                                 <Building2 size={16} className="text-[#2e2d29]" />}
                              </div>
                              {grup.name}
                            </div>
                          ) : (
                            <span className="text-xs text-[#8a7e70] pl-10">└ {a.currency} bakiye</span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm">{a.currency}</TableCell>
                        <TableCell className={`text-right font-bold font-mono ${(a.net_balance ?? 0) >= 0 ? 'text-[#3d6e4e]' : 'text-[#7a3535]'}`}>
                          {fmtTR(a.net_balance ?? 0)}
                        </TableCell>
                        <TableCell className="w-8">
                          {ilk && <ArrowRight size={14} className="text-[#d0c4b0] group-hover:text-[#1a1916] transition-colors" />}
                        </TableCell>
                      </TableRow>
                    );
                  }))}
                  {hesapGruplari.length === 0 && (
                    <TableRow><TableCell colSpan={4} className="text-center py-8 text-[#8a7e70]">Hesap yok</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── TRANSACTIONS ── */}
        <TabsContent value="transactions">
          <div className="flex gap-3 mb-4">
            <div className="w-72">
              <SearchableSelect
                options={allTargets}
                value={txnFilter.account_id === 'all' ? '' : txnFilter.account_id}
                onChange={v => {
                  setFiltreTaslak(p => ({ ...p, account_id: v || 'all', page: 1 }));
                  setTxnFilter(p => ({ ...p, account_id: v || 'all', page: 1 }));
                }}
                placeholder="Hesap ara (giren veya çıkan)..." />
            </div>
          </div>
          <div className="bg-[#faf7f2] border border-[#d0c4b0] rounded-lg overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#e8dfd0] hover:bg-[#e8dfd0]">
                  <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Tarih</TableHead>
                  <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Giren Hesap</TableHead>
                  <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Çıkan Hesap</TableHead>
                  <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right">Tutar</TableHead>
                  <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Açıklama</TableHead>
                  <TableHead className="w-16"></TableHead>
                </TableRow>
                {/* SÜTUN FİLTRELERİ — sunucuda uygulanır, tüm kayıtları kapsar */}
                <TableRow className="bg-[#f0e9dc] hover:bg-[#f0e9dc] border-[#d0c4b0]">
                  {/* Tarih: gerçek tarih kutuları (başlangıç / bitiş) */}
                  <TableCell className="py-1.5 px-2 align-top">
                    <div className="flex flex-col gap-1">
                      <input type="date" value={filtreTaslak.date_from}
                        onChange={e => filtreDegistir('date_from', e.target.value, true)}
                        title="Başlangıç tarihi"
                        className="h-7 w-[130px] px-1.5 rounded border border-[#d0c4b0] bg-white text-[11px] focus:outline-none focus:border-[#3d6e4e]"
                        data-testid="filtre-tarih-bas" />
                      <input type="date" value={filtreTaslak.date_to}
                        onChange={e => filtreDegistir('date_to', e.target.value, true)}
                        title="Bitiş tarihi"
                        className="h-7 w-[130px] px-1.5 rounded border border-[#d0c4b0] bg-white text-[11px] focus:outline-none focus:border-[#3d6e4e]"
                        data-testid="filtre-tarih-bit" />
                    </div>
                  </TableCell>
                  <TableCell className="py-1.5 px-2 align-top">
                    <input type="text" value={filtreTaslak.debit_search}
                      onChange={e => filtreDegistir('debit_search', e.target.value)}
                      placeholder="Hesap ara..."
                      className="h-7 w-full min-w-[110px] px-1.5 rounded border border-[#d0c4b0] bg-white text-[11px] focus:outline-none focus:border-[#3d6e4e]"
                      data-testid="filtre-giren" />
                  </TableCell>
                  <TableCell className="py-1.5 px-2 align-top">
                    <input type="text" value={filtreTaslak.credit_search}
                      onChange={e => filtreDegistir('credit_search', e.target.value)}
                      placeholder="Hesap ara..."
                      disabled={filtreTaslak.tek_tarafli}
                      className="h-7 w-full min-w-[110px] px-1.5 rounded border border-[#d0c4b0] bg-white text-[11px] focus:outline-none focus:border-[#3d6e4e] disabled:bg-[#f5f0e8]"
                      data-testid="filtre-cikan" />
                    <label className="mt-1 flex items-center gap-1 text-[10px] text-[#8a5c00] cursor-pointer select-none"
                      title="Karşı hesabı girilmemiş hareketleri listele (döviz işleminin ikinci bacağı gibi)">
                      <input type="checkbox" checked={!!filtreTaslak.tek_tarafli}
                        onChange={e => filtreDegistir('tek_tarafli', e.target.checked, true)}
                        className="accent-[#8a5c00]" data-testid="filtre-tek-tarafli" />
                      karşı hesabı yok
                    </label>
                  </TableCell>
                  {/* Tutar: karşılaştırma operatörü + değer (aralıkta iki değer) */}
                  <TableCell className="py-1.5 px-2 align-top">
                    <div className="flex flex-col gap-1 items-end">
                      <div className="flex gap-1">
                        <select value={filtreTaslak.amount_op}
                          onChange={e => filtreDegistir('amount_op', e.target.value, true)}
                          title="Karşılaştırma"
                          className="h-7 w-[62px] px-1 rounded border border-[#d0c4b0] bg-white text-[11px] font-mono focus:outline-none focus:border-[#3d6e4e]"
                          data-testid="filtre-tutar-op">
                          <option value="">—</option>
                          <option value="=">=</option>
                          <option value=">">&gt;</option>
                          <option value=">=">&ge;</option>
                          <option value="<">&lt;</option>
                          <option value="<=">&le;</option>
                          <option value="between">arası</option>
                        </select>
                        <input type="text" inputMode="decimal" value={filtreTaslak.amount}
                          onChange={e => filtreDegistir('amount', e.target.value)}
                          placeholder="0,00" disabled={!filtreTaslak.amount_op}
                          className="h-7 w-[86px] px-1.5 rounded border border-[#d0c4b0] bg-white text-[11px] text-right font-mono tabular-nums focus:outline-none focus:border-[#3d6e4e] disabled:bg-[#f5f0e8]"
                          data-testid="filtre-tutar" />
                      </div>
                      {filtreTaslak.amount_op === 'between' && (
                        <input type="text" inputMode="decimal" value={filtreTaslak.amount2}
                          onChange={e => filtreDegistir('amount2', e.target.value)}
                          placeholder="üst sınır"
                          className="h-7 w-[86px] px-1.5 rounded border border-[#d0c4b0] bg-white text-[11px] text-right font-mono tabular-nums focus:outline-none focus:border-[#3d6e4e]"
                          data-testid="filtre-tutar2" />
                      )}
                      {/* Para birimi — tutar sütununun parçası; farklı kurlardaki
                          kayıtlar birbirine karışmasın diye ayrı seçim. */}
                      <select value={filtreTaslak.currency}
                        onChange={e => filtreDegistir('currency', e.target.value, true)}
                        title="Para birimi"
                        className={`h-7 w-[152px] px-1 rounded border bg-white text-[11px] focus:outline-none focus:border-[#3d6e4e] ${filtreTaslak.currency ? 'border-[#3d6e4e] text-[#1a1916]' : 'border-[#d0c4b0] text-[#8a7e70]'}`}
                        data-testid="filtre-para-birimi">
                        <option value="">Tüm para birimleri</option>
                        {paraBirimleri.map(pb => <option key={pb} value={pb}>{pb}</option>)}
                      </select>
                    </div>
                  </TableCell>
                  <TableCell className="py-1.5 px-2 align-top">
                    <input type="text" value={filtreTaslak.description}
                      onChange={e => filtreDegistir('description', e.target.value)}
                      placeholder="Açıklamada ara..."
                      className="h-7 w-full min-w-[130px] px-1.5 rounded border border-[#d0c4b0] bg-white text-[11px] focus:outline-none focus:border-[#3d6e4e]"
                      data-testid="filtre-aciklama" />
                  </TableCell>
                  <TableCell className="py-1.5 px-1 align-top">
                    {filtreVarMi && (
                      <button type="button" onClick={filtreleriTemizle}
                        title="Filtreleri temizle" aria-label="Filtreleri temizle"
                        className="h-7 w-7 flex items-center justify-center rounded border border-[#d0c4b0] bg-white text-[#7a3535] hover:bg-[#7a3535]/10"
                        data-testid="filtre-temizle">
                        <X size={13} />
                      </button>
                    )}
                  </TableCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {transactions.items.map(t => (
                  <TableRow key={t.id} className="border-[#d0c4b0]">
                    <TableCell className="text-sm text-[#3F3E39]">{t.transaction_date}</TableCell>
                    <TableCell className="text-sm font-medium">
                      {t.debit_account_id ? (
                        <a href={`/erp/suppliers/${t.debit_account_id}`} target="_blank" rel="noopener noreferrer"
                          className="text-[#3d6e4e] hover:underline" title="Hesabı yeni sekmede aç">{t.debit_account_name}</a>
                      ) : <span className="text-[#3d6e4e]">{t.debit_account_name}</span>}
                    </TableCell>
                    <TableCell className="text-sm font-medium">
                      {t.credit_account_id ? (
                        <a href={`/erp/suppliers/${t.credit_account_id}`} target="_blank" rel="noopener noreferrer"
                          className="text-[#7a3535] hover:underline" title="Hesabı yeni sekmede aç">{t.credit_account_name}</a>
                      ) : t.tek_tarafli ? (
                        /* Döviz işleminin ikinci bacağı ve elle girilen bazı kayıtlarda
                           karşı hesap yazılmamış. Gizlemek yerine işaretliyoruz. */
                        <span className="inline-flex items-center gap-1 text-[11px] text-[#8a5c00] bg-[#7a6a2e]/10 border border-[#e0b45a] rounded px-1.5 py-0.5"
                          title="Bu hareketin karşı hesabı girilmemiş — paranın hangi kasa/bankadan çıktığı belli değil">
                          <AlertTriangle size={11} className="shrink-0" /> karşı hesap yok
                        </span>
                      ) : <span className="text-[#7a3535]">{t.credit_account_name}</span>}
                    </TableCell>
                    <TableCell className="text-right font-mono font-semibold text-[#1a1916]">{t.amount?.toFixed(2)} {t.currency}</TableCell>
                    <TableCell className="text-sm text-[#8a7e70] max-w-[200px] truncate">{t.description}</TableCell>
                    <TableCell>
                      <div className="flex items-center">
                        <Button variant="ghost" size="icon" title="Ödeme dekontunu görüntüle"
                          className="h-7 w-7 text-[#8a7e70] hover:text-[#2e6fa0] hover:bg-[#2e6fa0]/10"
                          disabled={dekontMesgul === t.id + '-g'}
                          onClick={() => dekontuGoster(t.id, false)} data-testid={`dekont-txn-${t.id}`}>
                          <Receipt size={14} />
                        </Button>
                        <EkDugmesi hareketId={t.id} sayi={t.ek_sayisi} onDegisti={fetchTxns} />
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-[#8a7e70] hover:text-[#1a1916] hover:bg-[#e8dfd0]"
                          onClick={() => setEditTxn({ ...t })} data-testid={`edit-txn-${t.id}`}>
                          <Pencil size={14} />
                        </Button>
                        {silmeYetkisi && (
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-[#8a7e70] hover:text-red-600 hover:bg-red-50"
                          onClick={() => handleDeleteTransaction(t.id)}>
                          <Trash2 size={14} />
                        </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {transactions.items.length === 0 && (
                  <TableRow><TableCell colSpan={6} className="text-center py-8 text-[#8a7e70]">
                    {filtreVarMi ? (
                      <span className="flex flex-col items-center gap-2">
                        Filtreye uyan hareket yok
                        <button type="button" onClick={filtreleriTemizle}
                          className="text-[#3d6e4e] hover:underline text-sm">Filtreleri temizle</button>
                      </span>
                    ) : 'Hareket yok'}
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          {/* Sayaç filtre uygulandığında da doğru: süzme sunucuda yapılıyor,
              "kayıt" sayısı TÜM eşleşmeleri gösterir, açık sayfayı değil. */}
          <div className="flex items-center justify-end gap-2 mt-3">
            <span className="text-sm text-[#8a7e70]">
              {transactions.total} kayıt{filtreVarMi ? ' (filtreli)' : ''}
            </span>
            {totalPages > 1 && (
              <>
                <Button variant="outline" size="icon" className="h-8 w-8" disabled={txnFilter.page <= 1}
                  onClick={() => setTxnFilter(p => ({ ...p, page: p.page - 1 }))}><ChevronLeft size={14} /></Button>
                <span className="text-sm text-[#8a7e70] tabular-nums">{txnFilter.page} / {totalPages}</span>
                <Button variant="outline" size="icon" className="h-8 w-8" disabled={txnFilter.page >= totalPages}
                  onClick={() => setTxnFilter(p => ({ ...p, page: p.page + 1 }))}><ChevronRight size={14} /></Button>
              </>
            )}
          </div>
        </TabsContent>

        {/* ── ÇEK VE SENETLER (components/CekSenet.js) ── */}
        <TabsContent value="checks">
          <CekSenet hesaplar={allSuppliers} kasaBanka={accounts} silmeYetkisi={silmeYetkisi} onDegisti={fetchAll} />
        </TabsContent>
      </Tabs>

      {/* ── NEW TRANSACTION ── */}
      <Dialog open={showTxn} onOpenChange={v => { setShowTxn(v); if (!v) { setTxType('transfer'); setTxnForm(p => ({ ...p, transaction_date: new Date().toISOString().split('T')[0], debit_account_id: '', credit_account_id: '', amount: '', description: '' })); setFxOut({ accountId: '', amount: '', currency: 'USD' }); setFxIn({ accountId: '', amount: '', currency: 'TRY' }); setFxDescSuffix(''); setYeniEkler([]); }}}>
        <DialogContent className="bg-[#faf7f2] border-[#d0c4b0] max-w-lg">
          <DialogHeader><DialogTitle className="text-[#1a1916] font-bold">Yeni Hareket</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {/* İşlem türü */}
            <div>
              <Label className="text-xs text-[#8a7e70] uppercase tracking-wide mb-1.5 block">İşlem Türü</Label>
              <div className="flex gap-1.5">
                {TX_TYPES.map(t => (
                  <button key={t.key} onClick={() => setTxType(t.key)}
                    className={`flex-1 py-1.5 px-2 rounded-lg text-xs font-semibold border transition-colors ${txType === t.key ? 'bg-[#2e2d29] text-[#f5f0e8] border-[#2e2d29]' : 'bg-white text-[#5a5650] border-[#d0c4b0] hover:border-[#8a7e70]'}`}>
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Tarih */}
            <div><Label>Tarih</Label><Input type="date" value={txnForm.transaction_date} onChange={e => setTxnForm(p => ({...p, transaction_date: e.target.value}))} className="bg-white border-[#d0c4b0]" /></div>

            {/* Döviz İşlemi */}
            {txType === 'fx' && (() => {
              const outAmt = parseFloat(String(fxOut.amount).replace(',', '.'));
              const inAmt  = parseFloat(String(fxIn.amount).replace(',', '.'));
              const rate   = outAmt && inAmt && fxOut.currency !== fxIn.currency ? inAmt / outAmt : null;
              const rateText = rate ? `1 ${fxOut.currency} = ${fmtRate(rate)} ${fxIn.currency}` : '';
              return (
                <>
                  <div className="bg-[#fff0f0]/60 border border-[#f0d0d0] rounded-xl p-3 space-y-2">
                    <div className="text-xs font-bold text-[#7a3535] uppercase tracking-wide">Çıkan Hesap</div>
                    <SearchableSelect options={allTargets} value={fxOut.accountId} onChange={v => setFxOut(p => ({...p, accountId: v}))} placeholder="Hesap seç veya ara..." />
                    <div className="flex gap-2">
                      <Input type="number" step="0.01" value={fxOut.amount} onChange={e => setFxOut(p => ({...p, amount: e.target.value}))} placeholder="Tutar" className="bg-white border-[#d0c4b0] text-right font-mono flex-1" />
                      <select value={fxOut.currency} onChange={e => setFxOut(p => ({...p, currency: e.target.value}))} className="h-9 px-2 rounded-md border border-[#d0c4b0] bg-white text-sm focus:outline-none w-20">
                        {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                  </div>
                  {rateText && <div className="text-center text-xs font-mono text-[#5a5650] bg-[#e8dfd0] rounded-lg py-1.5 px-3">{rateText}</div>}
                  <div className="bg-[#f0fff4]/60 border border-[#c8e6d0] rounded-xl p-3 space-y-2">
                    <div className="text-xs font-bold text-[#3d6e4e] uppercase tracking-wide">Giren Hesap</div>
                    <SearchableSelect options={allTargets} value={fxIn.accountId} onChange={v => setFxIn(p => ({...p, accountId: v}))} placeholder="Hesap seç veya ara..." />
                    <div className="flex gap-2">
                      <Input type="number" step="0.01" value={fxIn.amount} onChange={e => setFxIn(p => ({...p, amount: e.target.value}))} placeholder="Tutar" className="bg-white border-[#d0c4b0] text-right font-mono flex-1" />
                      <select value={fxIn.currency} onChange={e => setFxIn(p => ({...p, currency: e.target.value}))} className="h-9 px-2 rounded-md border border-[#d0c4b0] bg-white text-sm focus:outline-none w-20">
                        {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-[#8a7e70]">Açıklama {rateText && <span className="font-normal text-[#3d6e4e]">({rateText} otomatik eklendi)</span>}</Label>
                    <Input value={fxDescSuffix} onChange={e => setFxDescSuffix(e.target.value)} placeholder="Ek açıklama (isteğe bağlı)..." className="bg-white border-[#d0c4b0]" />
                  </div>
                </>
              );
            })()}

            {/* Transfer / Cari Ödeme */}
            {(txType === 'transfer' || txType === 'cari') && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-[#3d6e4e]">Giren Hesap</Label>
                    <SearchableSelect
                      options={allTargets}
                      value={txnForm.debit_account_id}
                      onChange={v => setTxnForm(p => ({...p, debit_account_id: v}))}
                      placeholder="Seç veya ara..." />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-[#7a3535]">Çıkan Hesap</Label>
                    <SearchableSelect
                      options={allTargets}
                      value={txnForm.credit_account_id}
                      onChange={v => setTxnForm(p => ({...p, credit_account_id: v}))}
                      placeholder="Seç veya ara..." />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5"><Label>Tutar</Label><Input type="number" step="0.01" value={txnForm.amount} onChange={e => setTxnForm(p => ({...p, amount: e.target.value}))} className="bg-white border-[#d0c4b0] text-right font-mono" /></div>
                  <div className="space-y-1.5"><Label>Para Birimi</Label>
                    <select value={txnForm.currency} onChange={e => setTxnForm(p => ({...p, currency: e.target.value}))} className="w-full h-9 px-2 rounded-md border border-[#d0c4b0] bg-white text-sm focus:outline-none">
                      {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                </div>
                <div className="space-y-1.5"><Label>Açıklama</Label><Input value={txnForm.description} onChange={e => setTxnForm(p => ({...p, description: e.target.value}))} className="bg-white border-[#d0c4b0]" /></div>
              </>
            )}
            <EkSecici dosyalar={yeniEkler} setDosyalar={setYeniEkler} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTxn(false)} className="border-[#d0c4b0]">İptal</Button>
            <Button onClick={saveTxn} disabled={loading} className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916]">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── EDIT TRANSACTION ── */}
      <DekontDialog dekontlar={dekontlar} onClose={() => setDekontlar(null)} />

      <Dialog open={!!editTxn} onOpenChange={v => { if (!v) setEditTxn(null); }}>
        <DialogContent className="bg-[#faf7f2] border-[#d0c4b0] max-w-lg">
          <DialogHeader><DialogTitle className="text-[#1a1916] font-bold">Hareketi Düzelt</DialogTitle></DialogHeader>
          {editTxn && (
            <div className="space-y-4">
              <div><Label>Tarih</Label><Input type="date" value={editTxn.transaction_date} onChange={e => setEditTxn(p => ({ ...p, transaction_date: e.target.value }))} className="bg-white border-[#d0c4b0]" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-[#3d6e4e]">Giren Hesap</Label>
                  <SearchableSelect options={allTargets} value={editTxn.debit_account_id || ''} onChange={v => setEditTxn(p => ({ ...p, debit_account_id: v }))} placeholder="Seç veya ara..." />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-[#7a3535]">Çıkan Hesap</Label>
                  <SearchableSelect options={allTargets} value={editTxn.credit_account_id || ''} onChange={v => setEditTxn(p => ({ ...p, credit_account_id: v }))} placeholder="Seç veya ara..." />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5"><Label>Tutar</Label><Input type="number" step="0.01" value={editTxn.amount} onChange={e => setEditTxn(p => ({ ...p, amount: e.target.value }))} className="bg-white border-[#d0c4b0] text-right font-mono" /></div>
                <div className="space-y-1.5"><Label>Para Birimi</Label>
                  <select value={editTxn.currency} onChange={e => setEditTxn(p => ({ ...p, currency: e.target.value }))} className="w-full h-9 px-2 rounded-md border border-[#d0c4b0] bg-white text-sm focus:outline-none">
                    {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div className="space-y-1.5"><Label>Açıklama</Label><Input value={editTxn.description || ''} onChange={e => setEditTxn(p => ({ ...p, description: e.target.value }))} className="bg-white border-[#d0c4b0]" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTxn(null)} className="border-[#d0c4b0]">İptal</Button>
            <Button onClick={saveEditTxn} disabled={loading} className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916]">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── NEW ACCOUNT ── */}
      <Dialog open={showAccount} onOpenChange={v => { setShowAccount(v); if (!v) setAcctForm({ name: '', currency: 'TRY' }); }}>
        <DialogContent className="bg-[#faf7f2] border-[#d0c4b0]">
          <DialogHeader><DialogTitle className="text-[#1a1916] font-bold">Yeni Kasa / Banka Hesabı</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Hesap Adı</Label><Input value={acctForm.name} onChange={e => setAcctForm(p => ({...p, name: e.target.value}))} className="bg-white border-[#d0c4b0]" placeholder="Hesap adı..." /></div>
            <div className="space-y-2"><Label>Para Birimi</Label>
              <Select value={acctForm.currency} onValueChange={v => setAcctForm(p => ({...p, currency: v}))}>
                <SelectTrigger className="bg-white border-[#d0c4b0]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="TRY">TRY</SelectItem>
                  <SelectItem value="USD">USD</SelectItem>
                  <SelectItem value="EUR">EUR</SelectItem>
                  <SelectItem value="GBP">GBP</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAccount(false)} className="border-[#d0c4b0]">İptal</Button>
            <Button onClick={saveAccount} disabled={loading} className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916]">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── BANK IMPORT ── */}
      {/* Kapatma korumalı: eşleştirme yapılmış bir önizleme varsa kazara kapanmaz.
          (Dış tıklama / ESC ile kapanıp emeğin silinmesi sorunu yaşandı.) */}
      <Dialog open={showImport} onOpenChange={v => {
        if (!v && importData?.items?.length) {
          const pending = importData.items.filter(it => !it.imported && !it.is_duplicate).length;
          const done = importData.items.filter(it => it.imported).length;
          if (pending > 0) {
            const ok = window.confirm(
              `Ekstre önizlemesini kapatmak üzeresiniz.\n\n` +
              `Aktarılmayan ${pending} satırdaki eşleştirmeler KAYBOLACAK.` +
              (done ? `\nAktarılan ${done} satır kayıtlıdır, etkilenmez.` : '') +
              `\n\nKapatılsın mı?`
            );
            if (!ok) return;   // pencere açık kalır, veri korunur
          }
        }
        setShowImport(v);
        if (!v) resetImport();
      }}>
        <DialogContent
          /* Önizlemedeyken dışarı tıklama ve ESC pencereyi KAPATMAZ —
             eşleştirmeler yalnızca bilinçli olarak (Geri / X / Aktar) bırakılır. */
          onPointerDownOutside={e => { if (importData?.items?.length) e.preventDefault(); }}
          onInteractOutside={e => { if (importData?.items?.length) e.preventDefault(); }}
          onEscapeKeyDown={e => { if (importData?.items?.length) e.preventDefault(); }}
          className={`bg-[#faf7f2] border-[#d0c4b0] flex flex-col ${
          importData
            ? 'max-w-none w-[97vw] h-[94vh] p-4'   /* önizleme: tam ekran — açıklamalar sığsın */
            : 'max-w-lg max-h-[85vh] overflow-y-auto' /* dosya seçme adımı: küçük kalsın */
        }`}>
          <DialogHeader className="shrink-0"><DialogTitle className="text-[#1a1916] font-bold">Banka Ekstresi Aktar</DialogTitle></DialogHeader>
          {!importData ? (
            <div className="space-y-4 py-4">
              {/* Adım 1: Hedef hesap */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#2e2d29] text-[#f5f0e8] text-xs font-bold">1</span>
                  Hedef Hesap
                </Label>
                <Select value={importAccountId || undefined} onValueChange={v => { setImportAccountId(v); setImportError(''); }}>
                  <SelectTrigger className="bg-white border-[#d0c4b0]"><SelectValue placeholder="Banka / kasa hesabı seçin" /></SelectTrigger>
                  <SelectContent>{accounts.map(a => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.name}{a.currency ? ` · ${a.currency}` : ''}
                    </SelectItem>
                  ))}</SelectContent>
                </Select>
                {/* EKSTRE HESABIN PARA BİRİMİNDE OKUNUR. Bir dönem her şey TL
                    yazılıyordu ve döviz hesaplarındaki 88 hareket TL sanıldı
                    (2026-09-21). Kullanıcı hangi kurda aktardığını görmeli. */}
                {(() => {
                  if (!importAccountId) return null;
                  const h = accounts.find(a => a.id === importAccountId);
                  if (!h) return null;
                  return h.currency ? (
                    <p className="text-xs text-[#3d6e4e]">
                      Hareketler <b>{h.currency}</b> olarak aktarılacak
                      {h.currency !== 'TRY' && ' (TL karşılığı işlem tarihinin TCMB kuruyla yazılır)'}.
                    </p>
                  ) : (
                    <p className="text-xs text-[#7a3535]">
                      Bu hesabın para birimi tanımlı değil — aktarım TL olarak yapılır.
                      Döviz hesabıysa önce hesabın para birimini düzeltin.
                    </p>
                  );
                })()}
              </div>

              {/* Adım 2: Dosya — hesap seçilene kadar pasif */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-xs font-bold ${importAccountId ? 'bg-[#2e2d29] text-[#f5f0e8]' : 'bg-[#d0c4b0] text-[#8a7e70]'}`}>2</span>
                  Ekstre Dosyası
                </Label>
                {!importAccountId ? (
                  <div className="border-2 border-dashed border-[#d0c4b0] rounded-lg p-6 text-center bg-[#efe9df]/60 cursor-not-allowed select-none">
                    <Upload size={28} className="mx-auto text-[#c0b8ad] mb-2" />
                    <p className="text-sm text-[#8a7e70]">Önce yukarıdan hedef hesabı seçin</p>
                  </div>
                ) : (
                  <div
                    onClick={() => !loading && importFileRef.current?.click()}
                    onDragOver={e => { e.preventDefault(); if (!loading) setDragActive(true); }}
                    onDragLeave={e => { e.preventDefault(); setDragActive(false); }}
                    onDrop={handleImportDrop}
                    className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${loading ? 'cursor-wait' : 'cursor-pointer'} ${dragActive ? 'border-[#3d6e4e] bg-[#3d6e4e]/5' : 'border-[#8a7e70] bg-white hover:border-[#3d6e4e] hover:bg-[#3d6e4e]/5'}`}>
                    {loading ? (
                      <>
                        <Loader2 size={28} className="mx-auto text-[#3d6e4e] mb-2 animate-spin" />
                        <p className="text-sm text-[#1a1916] font-medium">AI okuyor…</p>
                        {importFileName && <p className="text-xs text-[#8a7e70] mt-1 flex items-center justify-center gap-1"><FileText size={12} /> {importFileName}</p>}
                      </>
                    ) : importFileName ? (
                      <>
                        <Check size={28} className="mx-auto text-[#3d6e4e] mb-2" />
                        <p className="text-sm text-[#1a1916] font-medium flex items-center justify-center gap-1.5"><FileText size={14} /> {importFileName}</p>
                        <p className="text-xs text-[#8a7e70] mt-1">Değiştirmek için tıklayın veya yeni dosya sürükleyin</p>
                      </>
                    ) : (
                      <>
                        <Upload size={28} className={`mx-auto mb-2 ${dragActive ? 'text-[#3d6e4e]' : 'text-[#8a7e70]'}`} />
                        <p className="text-sm text-[#1a1916] font-medium">{dragActive ? 'Dosyayı bırakın' : 'Dosyayı buraya sürükleyin veya tıklayıp seçin'}</p>
                        <p className="text-xs text-[#8a7e70] mt-0.5">Excel (.xlsx) veya CSV — AI otomatik okuyacak</p>
                      </>
                    )}
                    <input ref={importFileRef} type="file" accept=".xlsx,.xls,.csv" onChange={handleImportFile} className="hidden" />
                  </div>
                )}
              </div>

              {importError && (
                <div className="flex items-start gap-2 rounded-lg border border-[#e0b4b4] bg-[#7a3535]/8 px-3 py-2 text-sm text-[#7a3535]">
                  <AlertTriangle size={15} className="mt-0.5 flex-shrink-0" />
                  <span>{importError}</span>
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col gap-3 flex-1 min-h-0">
              {importData.previous_import && (
                <div className="flex items-start gap-2 rounded-lg border border-[#e0b45a] bg-[#7a6a2e]/10 px-3 py-2.5 text-sm text-[#7a6a2e] shrink-0">
                  <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold">Bu dosya bu hesaba daha önce aktarılmış</p>
                    <p className="text-xs mt-0.5">
                      {importData.previous_import.file_name || 'Aynı ekstre'} — {new Date(importData.previous_import.imported_at).toLocaleString('tr-TR')} tarihinde {importData.previous_import.imported_count} hareket aktarılmıştı.
                      Aşağıdaki mükerrer satırlar otomatik atlanacak, yine de kontrol edin.
                    </p>
                  </div>
                </div>
              )}
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm text-[#8a7e70]">
                    {importData.total} satır okundu
                    <span className="ml-2 font-semibold text-[#1a1916]">
                      {importData.items.filter(it => it.selected && isRowActionable(it)).length} seçili
                    </span>
                    {importData.items.some(it => it.imported) && (
                      <span className="ml-2 text-[#3d6e4e] font-semibold">
                        · {importData.items.filter(it => it.imported).length} aktarıldı
                      </span>
                    )}
                  </p>
                  {importData.duplicate_count > 0 && (
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-[#7a6a2e]/10 text-[#7a6a2e]">
                      <AlertTriangle size={12} /> {importData.duplicate_count} mükerrer bulundu (aktarılmayacak)
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {importData.items.some(it => it.suggested_supplier_id) && (
                    <Button size="sm" variant="outline" onClick={applyAllImportSuggestions}
                      className="h-7 text-xs border-[#3d6e4e] text-[#3d6e4e] hover:bg-[#3d6e4e]/10">
                      <Sparkles size={12} className="mr-1" />
                      Tüm eşleştirme önerilerini uygula ({importData.items.filter(it => it.suggested_supplier_id).length})
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={refreshSuppliers}
                    title="Başka sekmede cari eklediyseniz listeyi tazeleyin — eşleştirmeleriniz korunur"
                    className="h-7 text-xs border-[#d0c4b0] text-[#5a5650] hover:bg-[#e8dfd0]">
                    <RefreshCw size={12} className="mr-1" /> Cari listesini yenile
                  </Button>
                </div>
              </div>
              {/* Yeni cari oluşturma — import penceresinin İÇİNDE açılır.
                  (Dışarıda ayrı bir katman olarak açılırsa Radix Dialog onu
                  etkileşime kapatır ve kapatma tıklaması alttaki pencereyi
                  kapatıp eşleştirmeleri siler.) */}
              {newSupplier.open && (
                <div className="shrink-0 rounded-lg border-2 border-[#3d6e4e] bg-white p-4 shadow-sm">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div>
                      <h3 className="text-sm font-bold text-[#1a1916]">Yeni Cari Hesap</h3>
                      <p className="text-xs text-[#8a7e70] mt-0.5">
                        Oluşturulan cari listeye eklenir ve seçili satıra atanır — eşleştirmeleriniz korunur.
                      </p>
                    </div>
                    <button type="button" onClick={() => setNewSupplier(p => ({ ...p, open: false }))}
                      disabled={newSupplier.saving} aria-label="Kapat"
                      className="text-[#8a7e70] hover:text-[#1a1916] shrink-0">
                      <X size={16} />
                    </button>
                  </div>
                  {newSupplier.rowIndex != null && importData?.items?.[newSupplier.rowIndex] && (
                    <div className="rounded-md bg-[#faf7f2] border border-[#d0c4b0] px-3 py-1.5 text-xs mb-3">
                      <span className="text-[#8a7e70]">Ekstre satırı: </span>
                      <span className="text-[#1a1916]">{importData.items[newSupplier.rowIndex].description}</span>
                    </div>
                  )}
                  <div className="flex flex-wrap items-end gap-3">
                    <div className="flex-1 min-w-[240px] space-y-1">
                      <Label className="text-xs font-semibold text-[#5a5650]">Cari Adı</Label>
                      <Input autoFocus value={newSupplier.name}
                        onChange={e => setNewSupplier(p => ({ ...p, name: e.target.value }))}
                        onKeyDown={e => { if (e.key === 'Enter' && !newSupplier.saving && newSupplier.name.trim()) createSupplierInline(); }}
                        placeholder="Örn: ABC Gıda Ltd."
                        className="bg-white border-[#d0c4b0] h-9" data-testid="new-supplier-name" />
                    </div>
                    <div className="w-[220px] space-y-1">
                      <Label className="text-xs font-semibold text-[#5a5650]">Hesap Türü</Label>
                      <select value={newSupplier.account_type}
                        onChange={e => setNewSupplier(p => ({ ...p, account_type: e.target.value }))}
                        className="w-full h-9 px-2 rounded-md border border-[#d0c4b0] bg-white text-sm focus:outline-none focus:border-[#3d6e4e]">
                        {/* Tür listesi tek yerde (lib/hesapTurleri.js); burada ayrı bir
                            liste eski 'Tedarikçiler ve Satıcılar'ı yazmaya devam ediyordu */}
                        {YENI_HESAP_TURLERI.map(t => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" disabled={newSupplier.saving}
                        onClick={() => setNewSupplier(p => ({ ...p, open: false }))}
                        className="border-[#d0c4b0] h-9 text-xs">İptal</Button>
                      <Button onClick={createSupplierInline} disabled={newSupplier.saving || !newSupplier.name.trim()}
                        className="bg-[#3d6e4e] text-white hover:bg-[#2d5e3e] h-9 text-xs font-semibold"
                        data-testid="new-supplier-save">
                        {newSupplier.saving ? 'Oluşturuluyor…' : 'Oluştur ve Ata'}
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Tablo kendi içinde kayar; başlık sabit kalır */}
              <div className="flex-1 min-h-0 overflow-auto rounded-lg border border-[#d0c4b0] bg-white">
              <Table>
                <TableHeader className="sticky top-0 z-10"><TableRow className="bg-[#e8dfd0] hover:bg-[#e8dfd0]">
                  <TableHead className="w-[40px] px-2">
                    <input type="checkbox" aria-label="Tümünü seç"
                      title="Aktarılabilir tüm satırları seç / kaldır"
                      className="accent-[#3d6e4e] cursor-pointer"
                      checked={importData.items.some(isRowActionable) && importData.items.filter(isRowActionable).every(it => it.selected)}
                      onChange={e => toggleAllImportRows(e.target.checked)} />
                  </TableHead>
                  <TableHead className="text-[#1a1916] font-bold text-xs uppercase w-[92px]">Tarih</TableHead>
                  <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Aciklama</TableHead>
                  <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right w-[110px]">Tutar</TableHead>
                  <TableHead className="text-[#1a1916] font-bold text-xs uppercase w-[76px]">Yon</TableHead>
                  <TableHead className="text-[#1a1916] font-bold text-xs uppercase w-[260px]">Cari Eslestirme</TableHead>
                  <TableHead className="text-[#1a1916] font-bold text-xs uppercase w-[240px]">Gider Kaydı</TableHead>
                  <TableHead className="text-[#1a1916] font-bold text-xs uppercase w-[110px]">Durum</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {importData.items.map((item, i) => (
                    <TableRow key={i} className={`border-[#d0c4b0] ${
                      item.imported ? 'bg-[#3d6e4e]/[0.06] opacity-60'
                        : (item.is_duplicate && !item.duplicate_override) ? 'bg-[#7a6a2e]/10' : ''
                    }`}>
                      <TableCell className="px-2 align-top">
                        <input type="checkbox"
                          className="accent-[#3d6e4e] cursor-pointer disabled:cursor-not-allowed"
                          checked={!!item.selected}
                          disabled={!isRowActionable(item)}
                          title={item.imported ? 'Bu satır aktarıldı'
                            : item.is_duplicate ? 'Mükerrer görünüyor — seçmek için onay istenir'
                            : 'Aktarım için seç'}
                          aria-label={`Satır ${i + 1} seç`}
                          onChange={e => toggleImportRow(i, e.target.checked)} />
                      </TableCell>
                      <TableCell className="text-sm whitespace-nowrap align-top">{item.date}</TableCell>
                      {/* Açıklama tam görünür — gerekirse alt satıra kayar */}
                      <TableCell className="text-sm text-[#3F3E39] align-top whitespace-normal break-words leading-snug py-2">{item.description}</TableCell>
                      <TableCell className={`text-right font-mono tabular-nums font-semibold whitespace-nowrap align-top ${item.direction === 'in' ? 'text-[#3d6e4e]' : 'text-[#7a3535]'}`}>{Number(item.amount || 0).toFixed(2)}</TableCell>
                      <TableCell className="align-top"><span className={`status-badge ${item.direction === 'in' ? 'bg-[#3d6e4e]/10 text-[#3d6e4e]' : 'bg-[#7a3535]/10 text-[#7a3535]'}`}>{item.direction === 'in' ? 'Giris' : 'Cikis'}</span></TableCell>
                      <TableCell className="align-top">
                        <div className="flex items-center gap-1">
                          <select
                            value={item.matched_supplier_id || ''}
                            onChange={e => updateImportItemSupplier(i, e.target.value)}
                            disabled={item.imported}
                            className={`flex-1 min-w-0 h-8 px-1.5 rounded-md border bg-white text-xs focus:outline-none focus:border-[#3d6e4e] disabled:bg-[#f5f0e8] disabled:cursor-not-allowed ${item.matched_supplier_id ? 'border-[#d0c4b0]' : 'border-[#e0b45a] text-[#8a7e70]'}`}>
                            <option value="">— Eşleşmedi —</option>
                            {allTargets.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                          </select>
                          <button type="button"
                            onClick={() => setNewSupplier({ open: true, rowIndex: i, name: item.description?.slice(0, 60) || '', account_type: VARSAYILAN_HESAP_TURU, saving: false })}
                            disabled={item.imported}
                            title="Bu satır için yeni cari oluştur (sayfadan çıkmadan)"
                            aria-label="Yeni cari oluştur"
                            className="h-8 w-8 shrink-0 flex items-center justify-center rounded-md border border-[#d0c4b0] bg-white text-[#3d6e4e] hover:bg-[#3d6e4e]/10 hover:border-[#3d6e4e] transition-colors">
                            <Plus size={13} />
                          </button>
                        </div>
                        {item.suggested_supplier_id && (
                          <button type="button" onClick={() => applyImportSuggestion(i)}
                            className="mt-1 flex items-center gap-1 text-[10px] text-[#3d6e4e] hover:underline">
                            <Sparkles size={9} className="shrink-0" />
                            <span className="truncate">{item.suggested_supplier_name} uygula?</span>
                          </button>
                        )}
                      </TableCell>
                      <TableCell className="align-top">
                        <label className="flex items-center gap-1.5 text-[11px] text-[#5a5650] cursor-pointer select-none">
                          <input type="checkbox" checked={!!item.is_expense}
                            onChange={e => toggleImportItemExpense(i, e.target.checked)}
                            disabled={item.imported}
                            className="accent-[#3d6e4e] disabled:cursor-not-allowed" />
                          Gider olarak da işle
                          {item.suggested_expense_item_id && (
                            <Sparkles size={10}
                              className={item.expense_suggestion_source === 'ogrenildi'
                                ? 'text-[#7a6a2e]' : 'text-[#3d6e4e]'}
                              title={item.expense_suggestion_source === 'ogrenildi'
                                ? `Daha önce bu tür satır için "${item.suggested_expense_item_name}" seçmiştiniz`
                                : `Öneri: ${item.suggested_expense_item_name}`} />
                          )}
                        </label>
                        {item.expense_suggestion_source === 'ogrenildi' && !item.imported && (
                          <p className="text-[10px] text-[#7a6a2e] leading-tight mt-0.5">
                            geçmiş seçiminizden
                          </p>
                        )}
                        {item.is_expense && (
                          <div className="mt-1 space-y-1">
                            <select
                              value={item.expense_item_id || ''}
                              onChange={e => updateImportItemExpenseField(i, 'expense_item_id', e.target.value)}
                              disabled={item.imported}
                              className={`w-full h-7 px-1.5 rounded-md border bg-white text-[11px] focus:outline-none focus:border-[#3d6e4e] ${item.expense_item_id ? 'border-[#d0c4b0]' : 'border-[#e0b45a] text-[#8a7e70]'}`}>
                              <option value="">— Gider kalemi seç —</option>
                              {expenseItems.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                            </select>
                            <input type="text" placeholder="Fatura no (opsiyonel)"
                              value={item.invoice_number || ''}
                              onChange={e => updateImportItemExpenseField(i, 'invoice_number', e.target.value)}
                              disabled={item.imported}
                              className="w-full h-7 px-1.5 rounded-md border border-[#d0c4b0] bg-white text-[11px] focus:outline-none focus:border-[#3d6e4e]" />
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="align-top">{
                        item.imported
                          ? <span className="text-xs font-semibold text-[#3d6e4e] flex items-center gap-1"><Check size={12} /> Aktarıldı</span>
                          : item.duplicate_override
                            ? <span className="text-xs text-[#3d6e4e] flex items-start gap-1 whitespace-normal leading-snug"
                                title="Mükerrer uyarısı kullanıcı tarafından geçersiz sayıldı">
                                <Check size={12} className="shrink-0 mt-0.5" /> Mükerrer değil (siz onayladınız)
                              </span>
                          : item.is_duplicate
                            ? <span className="text-xs text-[#7a6a2e] flex items-start gap-1 whitespace-normal leading-snug" title={item.duplicate_reason || ''}><AlertTriangle size={12} className="shrink-0 mt-0.5" /> Mükerrer olabilir</span>
                            : <Check size={14} className="text-[#3d6e4e]" />
                      }</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              </div>
            </div>
          )}
          <DialogFooter className="shrink-0">
            {importData && (
              <>
                <Button variant="outline" className="border-[#d0c4b0]"
                  onClick={() => {
                    if (!window.confirm('Dosya seçimine dönülecek.\n\nYaptığınız eşleştirmeler kaybolacak. Devam edilsin mi?')) return;
                    resetImport();
                  }}>Geri</Button>
                {(() => {
                  const selCount = importData.items.filter(it => it.selected && isRowActionable(it)).length;
                  const remaining = importData.items.filter(isRowActionable).length;
                  return (
                    <Button onClick={confirmImport} disabled={loading || selCount === 0}
                      className="bg-[#3d6e4e] text-white hover:bg-[#2d5e3e] disabled:opacity-50"
                      title={selCount === 0 ? 'Aktarmak için satır seçin' : undefined}>
                      {loading ? 'Aktarılıyor…'
                        : selCount === remaining ? `Tümünü Aktar (${selCount})`
                        : `Seçilenleri Aktar (${selCount})`}
                    </Button>
                  );
                })()}
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mükerrer itirazı — yanlışlıkla çift kayıt açılmasın diye onay istenir */}
      <Dialog open={!!mukerrerOnay} onOpenChange={(a) => !a && setMukerrerOnay(null)}>
        <DialogContent className="bg-[#faf7f2] border-[#d0c4b0] max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#1a1916] flex items-center gap-2">
              <AlertTriangle size={18} className="text-[#7a6a2e]" />
              Bu satır mükerrer görünüyor
            </DialogTitle>
          </DialogHeader>
          {mukerrerOnay && (() => {
            const it = importData?.items?.[mukerrerOnay.idx] || {};
            return (
              <div className="space-y-3 text-sm">
                <div className="rounded-md border border-[#d0c4b0] bg-white p-3 space-y-1">
                  <div className="flex justify-between gap-3">
                    <span className="text-[#8a7e70]">{it.date}</span>
                    <span className="font-mono tabular-nums font-semibold">
                      {Number(it.amount || 0).toFixed(2)} TRY
                    </span>
                  </div>
                  <p className="text-[#3F3E39] leading-snug">{it.description}</p>
                </div>
                <div className="rounded-md bg-[#7a6a2e]/10 border border-[#7a6a2e]/30 p-3">
                  <p className="text-[11px] font-semibold text-[#7a6a2e] uppercase mb-1">Sebep</p>
                  <p className="text-[#3F3E39] leading-snug">{it.duplicate_reason || 'Aynı tarih ve tutarda bir kayıt bulundu.'}</p>
                </div>
                <p className="text-[#8a7e70] leading-snug">
                  Mükerrer tespiti <strong>tarih + tutar + hesap</strong> üçlüsüne bakar; açıklamayı
                  karşılaştırmaz. Aynı gün aynı tutarda gerçekten iki ayrı hareket olabilir.
                  Bu satırın farklı bir hareket olduğundan eminseniz aktarabilirsiniz —
                  emin değilseniz önce hesap ekstresinden kontrol edin.
                </p>
              </div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setMukerrerOnay(null)}
              className="border-[#d0c4b0]">Vazgeç</Button>
            <Button
              onClick={() => { satiriSec(mukerrerOnay.idx, true); setMukerrerOnay(null); }}
              className="bg-[#7a6a2e] text-white hover:bg-[#5e5223]"
              data-testid="mukerrer-itiraz-onay">
              Mükerrer değil, aktar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
